(() => {
  const namespace = 'LumiNovaStyleGallery';

  if (window[namespace]?.scan) {
    window[namespace].scan(document);
    return;
  }

  const instances = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let productFormPromise = null;

  const ensureProductForm = (source) => {
    if (!source || customElements.get('product-form')) return Promise.resolve();
    if (productFormPromise) return productFormPromise;

    productFormPromise = new Promise((resolve) => {
      const absoluteSource = new URL(source, document.baseURI).href;
      const existing = Array.from(document.scripts).find((script) => script.src === absoluteSource);
      const script = existing || document.createElement('script');
      const finish = () => resolve();

      if (!existing) {
        script.src = source;
        script.defer = true;
        document.head.appendChild(script);
      }

      if (customElements.get('product-form')) {
        finish();
        return;
      }

      script.addEventListener('load', finish, { once: true });
      window.setTimeout(finish, 4000);
    });

    return productFormPromise;
  };

  class StyleGallery {
    constructor(root) {
      this.root = root;
      this.id = root.dataset.lnStyleGallery;
      this.track = root.querySelector('[data-ln-style-track]');
      this.modal = root.querySelector('[data-ln-style-modal]');
      this.dialog = root.querySelector('[data-ln-style-dialog]');
      this.modalBody = root.querySelector('[data-ln-style-modal-body]');
      this.previous = root.querySelector('[data-ln-style-prev]');
      this.next = root.querySelector('[data-ln-style-next]');
      this.progress = root.querySelector('[data-ln-style-progress]');
      this.lastTrigger = null;
      this.closeTimer = null;
      this.abortController = new AbortController();
      this.resizeObserver = null;
      ensureProductForm(root.dataset.productFormSrc);
      this.bind();
      this.updateControls();
    }

    bind() {
      const options = { signal: this.abortController.signal };

      this.root.addEventListener('click', (event) => this.onClick(event), options);
      this.root.addEventListener('change', (event) => this.onChange(event), options);
      this.root.addEventListener('keydown', (event) => this.onKeydown(event), options);
      this.root.addEventListener('submit', (event) => this.onSubmit(event), options);
      this.track?.addEventListener('scroll', () => this.updateControls(), { passive: true, signal: this.abortController.signal });

      if ('ResizeObserver' in window && this.track) {
        this.resizeObserver = new ResizeObserver(() => this.updateControls());
        this.resizeObserver.observe(this.track);
      }
    }

    onClick(event) {
      const openTrigger = event.target.closest('[data-ln-open-style]');
      if (openTrigger && this.root.contains(openTrigger)) {
        event.preventDefault();
        this.openModal(openTrigger.dataset.templateId, Number(openTrigger.dataset.productIndex || 0), openTrigger);
        return;
      }

      const closeTrigger = event.target.closest('[data-ln-style-close]');
      if (closeTrigger && this.root.contains(closeTrigger)) {
        event.preventDefault();
        this.closeModal();
        return;
      }

      const tab = event.target.closest('[data-ln-product-tab]');
      if (tab && this.modalBody?.contains(tab)) {
        this.setActiveProduct(tab.dataset.lnProductTab, true);
        return;
      }

      if (event.target.closest('[data-ln-style-prev]')) {
        this.scrollBy(-this.getStep());
      }

      if (event.target.closest('[data-ln-style-next]')) {
        this.scrollBy(this.getStep());
      }
    }

    onChange(event) {
      const select = event.target.closest('[data-ln-variant-select]');
      if (select && this.modalBody?.contains(select)) this.updateVariant(select);
    }

    onSubmit(event) {
      if (!this.modalBody?.contains(event.target) || this.root.dataset.closeAfterAdd !== 'true') return;
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => this.closeModal(), 1200);
    }

    onKeydown(event) {
      if (!this.modal || this.modal.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeModal();
        return;
      }

      const tab = event.target.closest('[data-ln-product-tab]');
      if (tab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        this.moveTabFocus(tab, event.key);
        return;
      }

      if (event.key === 'Tab') this.trapFocus(event);
    }

    getStep() {
      const card = this.track?.querySelector('[data-ln-style-card]');
      if (!card || !this.track) return 320;
      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap || 0);
      return card.getBoundingClientRect().width + gap;
    }

    scrollBy(left) {
      this.track?.scrollBy({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    updateControls() {
      if (!this.track) return;
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const position = Math.max(0, this.track.scrollLeft);

      if (this.previous) this.previous.disabled = maximum <= 2 || position <= 2;
      if (this.next) this.next.disabled = maximum <= 2 || position >= maximum - 2;

      if (this.progress) {
        const percentage = maximum > 0 ? 18 + (position / maximum) * 82 : 100;
        this.progress.style.width = `${Math.max(18, Math.min(100, percentage))}%`;
      }
    }

    openModal(templateId, productIndex, trigger) {
      const template = this.root.querySelector(`[data-ln-style-template="${CSS.escape(templateId)}"]`);
      if (!template || !this.modal || !this.modalBody || !this.dialog) return;

      this.lastTrigger = trigger;
      this.modalBody.replaceChildren(template.content.cloneNode(true));
      this.modal.hidden = false;
      this.dialog.scrollTop = 0;
      document.documentElement.classList.add('ln-style-modal-open');
      document.body.classList.add('ln-style-modal-open');
      this.setActiveProduct(productIndex);
      this.modalBody.querySelectorAll('[data-ln-variant-select]').forEach((select) => this.updateVariant(select));

      window.requestAnimationFrame(() => {
        this.modal.querySelector('[data-ln-style-close]')?.focus();
      });
    }

    closeModal({ restoreFocus = true } = {}) {
      if (!this.modal || !this.modalBody || this.modal.hidden) return;
      window.clearTimeout(this.closeTimer);
      this.modal.hidden = true;
      this.modalBody.replaceChildren();
      document.documentElement.classList.remove('ln-style-modal-open');
      document.body.classList.remove('ln-style-modal-open');

      if (restoreFocus && this.lastTrigger?.isConnected) this.lastTrigger.focus();
      this.lastTrigger = null;
    }

    setActiveProduct(requestedIndex, moveFocus = false) {
      if (!this.modalBody) return;
      const tabs = Array.from(this.modalBody.querySelectorAll('[data-ln-product-tab]'));
      if (!tabs.length) return;
      const requested = String(requestedIndex);
      const activeTab = tabs.find((tab) => tab.dataset.lnProductTab === requested) || tabs[0];
      const activeIndex = activeTab.dataset.lnProductTab;

      tabs.forEach((tab) => {
        const active = tab === activeTab;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      this.modalBody.querySelectorAll('[data-ln-product-panel]').forEach((panel) => {
        const active = panel.dataset.lnProductPanel === activeIndex;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });

      if (moveFocus) activeTab.focus();
    }

    moveTabFocus(currentTab, key) {
      const tabs = Array.from(this.modalBody?.querySelectorAll('[data-ln-product-tab]') || []);
      if (!tabs.length) return;
      const currentIndex = Math.max(0, tabs.indexOf(currentTab));
      let nextIndex = currentIndex;

      if (key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      if (key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (key === 'Home') nextIndex = 0;
      if (key === 'End') nextIndex = tabs.length - 1;

      this.setActiveProduct(tabs[nextIndex].dataset.lnProductTab, true);
    }

    updateVariant(select) {
      const option = select.options[select.selectedIndex];
      const panel = select.closest('[data-ln-product-panel]');
      if (!option || !panel) return;

      const image = panel.querySelector('[data-ln-modal-product-image] img');
      const originalPrice = panel.querySelector('[data-ln-modal-original-price]');
      const salePrice = panel.querySelector('[data-ln-modal-sale-price]');
      const availability = panel.querySelector('[data-ln-modal-availability]');
      const addButton = panel.querySelector('.ln-style-atc');
      const addButtonText = addButton?.querySelector('[data-ln-atc-label]');
      const showDiscount = panel.dataset.showDiscountedPrices === 'true';
      const isAvailable = option.dataset.available === 'true';

      if (image && option.dataset.image) {
        image.src = option.dataset.image;
        image.removeAttribute('srcset');
      }

      if (showDiscount) {
        if (originalPrice) {
          originalPrice.textContent = option.dataset.price || '';
          originalPrice.hidden = false;
        }
        if (salePrice) salePrice.textContent = option.dataset.salePrice || option.dataset.price || '';
      } else {
        if (originalPrice) {
          originalPrice.textContent = option.dataset.comparePrice || '';
          originalPrice.hidden = !option.dataset.comparePrice;
        }
        if (salePrice) salePrice.textContent = option.dataset.price || '';
      }

      if (availability) {
        availability.textContent = isAvailable ? availability.dataset.availableLabel : availability.dataset.soldOutLabel;
        availability.dataset.available = String(isAvailable);
      }

      if (addButton) {
        addButton.disabled = !isAvailable;
        addButton.setAttribute('aria-disabled', String(!isAvailable));
      }

      if (addButtonText) {
        addButtonText.textContent = isAvailable ? this.root.dataset.addLabel : this.root.dataset.soldOutLabel;
      }
    }

    trapFocus(event) {
      const focusable = Array.from(this.dialog?.querySelectorAll('a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])') || [])
        .filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    destroy() {
      this.closeModal({ restoreFocus: false });
      window.clearTimeout(this.closeTimer);
      this.resizeObserver?.disconnect();
      this.abortController.abort();
    }
  }

  const api = {
    scan(container = document) {
      const roots = [];
      if (container.matches?.('[data-ln-style-gallery]')) roots.push(container);
      container.querySelectorAll?.('[data-ln-style-gallery]').forEach((root) => roots.push(root));

      roots.forEach((root) => {
        const id = root.dataset.lnStyleGallery;
        if (!id || instances.has(id)) return;
        instances.set(id, new StyleGallery(root));
      });
    },

    unload(container) {
      const roots = [];
      if (container.matches?.('[data-ln-style-gallery]')) roots.push(container);
      container.querySelectorAll?.('[data-ln-style-gallery]').forEach((root) => roots.push(root));

      roots.forEach((root) => {
        const id = root.dataset.lnStyleGallery;
        instances.get(id)?.destroy();
        instances.delete(id);
      });
    }
  };

  window[namespace] = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => api.scan(document), { once: true });
  } else {
    api.scan(document);
  }

  document.addEventListener('shopify:section:load', (event) => api.scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => api.unload(event.target));
})();
